import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format as formatDateFns } from 'date-fns';
import { useAuth } from './AuthContext';
import { useAdmin } from './AdminContext';
import { calendarsApi, eventsApi } from '../services/cloudApi';
import { normalizeTimestamp } from '../utils/timestampUtils';
import { getLimitsForMembership, MEMBERSHIP_TYPES, UPGRADE_MESSAGES } from '../utils/membershipConstants';
import { showPlusGoldNotification, PlusLimits } from '../utils/plusGoldNotification';
import { socketManager, ConnectionState } from '../services/socketService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import i18n from '../i18n';

// UUID v4 generator
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const CalendarContext = createContext(null);

const normalizeCalendarRecord = (calendar) => ({
  id: calendar.id,
  name: calendar.name || i18n.t('calendar:defaults.calendar'),
  color: calendar.color || '#ffffff',
  description: calendar.description || '',
  createdAt: normalizeTimestamp(calendar.createdAt ?? calendar.created_at),
  updatedAt: normalizeTimestamp(calendar.updatedAt ?? calendar.updated_at ?? calendar.createdAt ?? calendar.created_at),
});

const normalizeEventRecord = (event) => ({
  id: event.id,
  calendarId: event.calendarId ?? event.calendar_id ?? null,
  title: event.title || '',
  description: event.description || '',
  startDate: event.startDate ?? event.start_date,
  endDate: event.endDate ?? event.end_date ?? event.startDate ?? event.start_date,
  startTime: event.startTime ?? event.start_time ?? null,
  endTime: event.endTime ?? event.end_time ?? null,
  allDay: Boolean(event.allDay ?? event.all_day),
  reminderDay: event.reminderDay ?? event.reminder_day ?? null,
  reminderHour: event.reminderHour ?? event.reminder_hour ?? null,
  repeatType: event.repeatType ?? event.repeat_type ?? 'none',
  repeatGroupId: event.repeatGroupId ?? event.repeat_group_id ?? null,
  sourceEventId: event.sourceEventId ?? event.source_event_id ?? null,
  sourceMessageId: event.sourceMessageId ?? event.source_message_id ?? null,
  location: event.location ?? null,
  locationLat: event.locationLat ?? event.location_lat ?? null,
  locationLon: event.locationLon ?? event.location_lon ?? null,
  createdAt: normalizeTimestamp(event.createdAt ?? event.created_at),
  updatedAt: normalizeTimestamp(event.updatedAt ?? event.updated_at ?? event.createdAt ?? event.created_at),
});

const sortByUpdatedDesc = (a, b) =>
  new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDateString = (dateStr, daysToAdd) => {
  if (!dateStr) return dateStr;
  const date = new Date(dateStr);
  date.setDate(date.getDate() + daysToAdd);
  return formatDate(date);
};

const shiftDateByMonths = (dateStr, monthsToAdd) => {
  if (!dateStr) return dateStr;
  const date = new Date(dateStr);
  const dayOfMonth = date.getDate();
  date.setMonth(date.getMonth() + monthsToAdd);
  if (date.getDate() !== dayOfMonth) {
    date.setDate(0);
  }
  return formatDate(date);
};

const shiftDateByYears = (dateStr, yearsToAdd) => {
  if (!dateStr) return dateStr;
  const date = new Date(dateStr);
  const month = date.getMonth();
  const day = date.getDate();
  date.setFullYear(date.getFullYear() + yearsToAdd);
  if (date.getMonth() !== month || date.getDate() !== day) {
    date.setMonth(month + 1, 0);
  }
  return formatDate(date);
};


const EVENT_MIGRATION_KEY_PREFIX = 'notesplus_calendar_event_calendar_fix_v1_';

// Kullanıcının üyelik tipini belirleyen helper fonksiyon
const getMembershipType = (user) => {
  if (!user) return MEMBERSHIP_TYPES.FREE;
  
  // Admin kontrolü
  if (
    user.role === 'admin' ||
    user.isAdmin === true ||
    user.isAdmin === 1 ||
    user.membershipType === 'admin' ||
    user.membership_type === 'admin'
  ) {
    return MEMBERSHIP_TYPES.ADMIN;
  }
  
  // Plus kontrolü
  if (
    user.role === 'plus' ||
    user.membershipType === 'plus' ||
    user.membership_type === 'plus'
  ) {
    const expiryRaw = user.plusExpiryDate || user.plus_expiry_date;
    if (expiryRaw) {
      const exp = new Date(expiryRaw);
      if (!Number.isNaN(exp.getTime()) && exp.getTime() <= Date.now()) {
        return MEMBERSHIP_TYPES.FREE;
      }
    }
    return MEMBERSHIP_TYPES.PLUS;
  }
  
  return MEMBERSHIP_TYPES.FREE;
};

const SPECIAL_DAY_DEFINITIONS = [
  { key: 'sevgililer-gunu', titleKey: 'valentinesDay', month: 2, day: 14 },
  { key: 'dunya-kadinlar-gunu', titleKey: 'internationalWomensDay', month: 3, day: 8 },
  { key: 'canakkale-zaferi', titleKey: 'canakkaleVictoryDay', month: 3, day: 18 },
  { key: 'ulusal-egemenlik', titleKey: 'nationalSovereigntyDay', month: 4, day: 23 },
  { key: 'emek-dayanisma', titleKey: 'labourDay', month: 5, day: 1 },
  { key: 'ataturku-anma', titleKey: 'youthAndSportsDay', month: 5, day: 19 },
  { key: 'demokrasi-birlik', titleKey: 'democracyDay', month: 7, day: 15 },
  { key: 'zafer-bayrami', titleKey: 'victoryDay', month: 8, day: 30 },
  { key: 'cumhuriyet-bayrami', titleKey: 'republicDay', month: 10, day: 29 },
  { key: 'ogretmenler-gunu', titleKey: 'teachersDay', month: 11, day: 24 },
  { key: 'yilbasi', titleKey: 'newYearsDay', month: 12, day: 31, endMonth: 1, endDay: 1 },
];

export const CalendarProvider = ({ children }) => {
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [events, setEvents] = useState([]);
  const { user, isAuthenticated, authToken, isPlusUser } = useAuth();
  const { getActiveToken, getActiveUserId, getActiveUser } = useAdmin();
  
  // Impersonate durumunda impersonate user'ı, değilse normal user'ı kullan
  const activeToken = getActiveToken ? getActiveToken() : authToken;
  const activeUserId = getActiveUserId ? getActiveUserId() : user?.id;
  const activeUser = getActiveUser ? getActiveUser() : user;
  // Race-condition safe merge: id is unique key; newer timestamp wins; equal timestamp prefer incoming (socket vs sync)
  const mergeEntities = useCallback((prev, incoming) => {
    if (!prev || prev.length === 0) {
      return (incoming || []).slice().sort(sortByUpdatedDesc);
    }
    if (!incoming || incoming.length === 0) {
      return prev;
    }
    const map = new Map();
    prev.forEach((item) => map.set(item.id, item));
    incoming.forEach((item) => {
      const normalized = { ...item };
      if (!normalized.id) return;
      const existing = map.get(normalized.id);
      if (!existing) {
        map.set(normalized.id, normalized);
        return;
      }
      const existingDeleted = existing.deletedAt || existing.deleted_at;
      const existingDeletedTs = existingDeleted
        ? (typeof existingDeleted === 'number' ? existingDeleted : new Date(existingDeleted).getTime() / 1000)
        : 0;
      if (existingDeletedTs > 0) return;
      const existingTs = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
      const incomingTs = new Date(normalized.updatedAt || normalized.createdAt || 0).getTime();
      if (incomingTs > existingTs || incomingTs === existingTs) {
        map.set(normalized.id, normalized);
      }
    });
    return Array.from(map.values()).sort(sortByUpdatedDesc);
  }, []);


  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleRemote = async (event) => {
      const { detail } = event || {};
      if (!detail || !detail.entity) {
        return;
      }

      if (detail.entity === 'calendars' && Array.isArray(detail.records) && detail.records.length) {
        const normalized = detail.records.map(normalizeCalendarRecord);
        
        
        setCalendars((prev) => mergeEntities(prev, normalized));
      }

      if (detail.entity === 'events' && Array.isArray(detail.records) && detail.records.length) {
        const normalized = detail.records.map(normalizeEventRecord);
        
        
        setEvents((prev) => mergeEntities(prev, normalized));
      }
    };

    const handleOperation = (event) => {
      const { detail } = event || {};
      const operation = detail?.operation;
      const result = detail?.result;
      if (!operation || !operation.entity) {
        return;
      }

      if (operation.entity === 'calendars') {
        if (operation.action === 'delete' && operation.payload?.id) {
          setCalendars((prev) => prev.filter((calendar) => calendar.id !== operation.payload.id));
          if (selectedCalendar?.id === operation.payload.id) {
            setSelectedCalendar(null);
          }
          return;
        }

        if (result?.calendars || result?.calendar) {
          const incoming = (result.calendars || [result.calendar]).map(normalizeCalendarRecord);
          setCalendars((prev) => mergeEntities(prev, incoming));
        }
      }

      if (operation.entity === 'events') {
        if (operation.action === 'delete' && operation.payload?.id) {
          setEvents((prev) => prev.filter((eventItem) => eventItem.id !== operation.payload.id));
          return;
        }

        if (result?.events || result?.event) {
          const incoming = (result.events || [result.event]).map(normalizeEventRecord);
          setEvents((prev) => mergeEntities(prev, incoming));
        }
      }
    };

    window.addEventListener('notesplus-sync-remote', handleRemote);
    window.addEventListener('notesplus-sync-operation', handleOperation);

    return () => {
      window.removeEventListener('notesplus-sync-remote', handleRemote);
      window.removeEventListener('notesplus-sync-operation', handleOperation);
    };
  }, [mergeEntities, selectedCalendar, isAuthenticated, activeUserId]);

  // Realtime: socket listeners for calendar + chat event sync (event:updated, event:response, calendar:event-updated)
  const [socketConnectionState, setSocketConnectionState] = useState(socketManager.getState());
  const activeUserIdRef = useRef(activeUserId);
  activeUserIdRef.current = activeUserId;
  useEffect(() => {
    const unsub = socketManager.subscribe(setSocketConnectionState);
    return unsub;
  }, []);
  useEffect(() => {
    if (!isAuthenticated || !activeUserId || socketConnectionState !== ConnectionState.CONNECTED) {
      return;
    }
    const socket = socketManager.getSocket();
    if (!socket) return;

    const handleEventUpdated = (data) => {
      const messageId = data.messageId || data.groupMessageId;
      const message = data.message || {};
      if (!messageId) return;
      setEvents((prev) =>
        prev.map((ev) => {
          const isChatEvent =
            ev.sourceEventId === messageId ||
            ev.sourceMessageId === messageId;
          if (!isChatEvent) return ev;
          const startStr = message.event_start || '';
          const endStr = message.event_end || message.event_start || '';
          const startDate = startStr && startStr.length >= 10 ? startStr.slice(0, 10) : ev.startDate;
          const endDate = endStr && endStr.length >= 10 ? endStr.slice(0, 10) : ev.endDate;
          const hasTime = !message.event_all_day && startStr.length > 10;
          const startTime = hasTime && startStr.length > 10 ? startStr.slice(11, 16) : null;
          const endTime = hasTime && endStr.length > 10 ? endStr.slice(11, 16) : null;
          return {
            ...ev,
            title: message.event_title ?? ev.title,
            description: message.event_description ?? ev.description,
            startDate,
            endDate,
            startTime,
            endTime,
            allDay: message.event_all_day ?? ev.allDay,
            location: message.event_location !== undefined ? message.event_location : ev.location,
            updatedAt: message.updated_at || new Date().toISOString(),
          };
        })
      );
    };

    const handleEventResponse = (data) => {
      if (data.userId !== activeUserIdRef.current) return;
      const messageId = data.messageId;
      if (!messageId) return;
      if (data.status === 'attending' && data.calendarEvent) {
        const one = normalizeEventRecord(data.calendarEvent);
        setEvents((prev) => mergeEntities(prev, [one]));
      } else if (data.status !== 'attending') {
        setEvents((prev) =>
          prev.filter(
            (e) => e.sourceEventId !== messageId && e.sourceMessageId !== messageId
          )
        );
      }
    };

    const handleCalendarEventUpdated = (payload) => {
      if (!payload) return;
      if (payload.action === 'deleted' && payload.eventId) {
        setEvents((prev) => prev.filter((e) => e.id !== payload.eventId));
        return;
      }
      if (payload.action === 'created' && payload.events && Array.isArray(payload.events)) {
        const normalized = payload.events.map(normalizeEventRecord);
        setEvents((prev) => mergeEntities(prev, normalized));
        return;
      }
      if ((payload.action === 'created' || payload.action === 'updated') && payload.event) {
        const one = normalizeEventRecord(payload.event);
        setEvents((prev) => mergeEntities(prev, [one]));
      }
    };

    socket.on('event:updated', handleEventUpdated);
    socket.on('event:response', handleEventResponse);
    socket.on('calendar:event-updated', handleCalendarEventUpdated);
    return () => {
      socket.off('event:updated', handleEventUpdated);
      socket.off('event:response', handleEventResponse);
      socket.off('calendar:event-updated', handleCalendarEventUpdated);
    };
  }, [isAuthenticated, activeUserId, socketConnectionState, mergeEntities]);

  // Daily 00:01 summary: once per day show "Bugün X adet etkinliğiniz mevcut" via global event
  const eventsRef = useRef(events);
  eventsRef.current = events;
  useEffect(() => {
    const DAILY_SUMMARY_KEY = 'notesplus_lastDailyNotificationDate';
    const id = setInterval(() => {
      const now = new Date();
      if (now.getHours() !== 0 || now.getMinutes() !== 1) return;
        const today = formatDateFns(now, 'yyyy-MM-dd');
      try {
        const last = localStorage.getItem(DAILY_SUMMARY_KEY);
        if (last === today) return;
        const list = eventsRef.current || [];
        const todayStart = new Date(today);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        const count = list.filter((ev) => {
          const start = new Date(ev.startDate);
          const end = new Date(ev.endDate || ev.startDate);
          return start <= todayEnd && end >= todayStart;
        }).length;
        if (count === 0) return;
        localStorage.setItem(DAILY_SUMMARY_KEY, today);
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('notesplus-daily-calendar-summary', { detail: { count } }));
        }
      } catch (e) {
        console.warn('Daily calendar summary check failed:', e);
      }
    }, 60000);
    return () => clearInterval(id);
  }, []);

  // İlk yükleme flag'i - sadece bir kez çalışsın
  const hasLoadedRef = React.useRef(false);
  const lastUserIdRef = React.useRef(null);

  // Kullanıcı giriş yaptığında flag'leri sıfırla
  useEffect(() => {
    const handleUserLogin = () => {
      hasLoadedRef.current = false;
      lastUserIdRef.current = null;
    };

    window.addEventListener('notesplus-user-logged-in', handleUserLogin);
    return () => {
      window.removeEventListener('notesplus-user-logged-in', handleUserLogin);
    };
  }, []);

  // Logout olduğunda takvim ve etkinlik verilerini anında temizle
  useEffect(() => {
    const handleLogout = () => {
      setCalendars([]);
      setEvents([]);
      setSelectedCalendar(null);
      hasLoadedRef.current = false;
      lastUserIdRef.current = null;
    };
    window.addEventListener('notesplus-user-logged-out', handleLogout);
    return () => window.removeEventListener('notesplus-user-logged-out', handleLogout);
  }, []);

  // Verileri yükle (sadece ilk yüklemede ve user değiştiğinde)
  useEffect(() => {
    // Token yoksa veri yükleme
    if (!activeToken || !isAuthenticated || !activeUserId) {
      setCalendars([]);
      setEvents([]);
      return;
    }
    
    const currentUserId = activeUserId;
    
    // Eğer aynı kullanıcı ve zaten yüklendiyse, tekrar yükleme
    if (hasLoadedRef.current && lastUserIdRef.current === currentUserId) {
      return;
    }

    const loadData = async () => {
      try {
        // API'den takvimleri ve etkinlikleri çek
        const [calendarsResponse, eventsResponse] = await Promise.all([
          calendarsApi.list({ token: activeToken }),
          eventsApi.list({ token: activeToken }),
        ]);
        
        const apiCalendars = calendarsResponse?.calendars || [];
        const apiEvents = eventsResponse?.events || [];
        
        // Convert to display format
        const formattedCalendars = apiCalendars.map((calendar) => ({
          ...normalizeCalendarRecord(calendar),
          createdAt: normalizeTimestamp(calendar.createdAt || calendar.created_at),
          updatedAt: normalizeTimestamp(calendar.updatedAt || calendar.updated_at || calendar.createdAt || calendar.created_at),
        }));
        
        const formattedEvents = apiEvents.map((event) => ({
          ...normalizeEventRecord(event),
          createdAt: normalizeTimestamp(event.createdAt || event.created_at),
          updatedAt: normalizeTimestamp(event.updatedAt || event.updated_at || event.createdAt || event.created_at),
        }));

        setCalendars(formattedCalendars);
        setEvents(formattedEvents);
        hasLoadedRef.current = true;
        lastUserIdRef.current = currentUserId;
      } catch (error) {
        console.error('Error loading data from API:', error);
        setEvents([]);
        setCalendars([]);
        hasLoadedRef.current = true;
        lastUserIdRef.current = currentUserId;
      }
    };

    loadData();
  }, [activeUserId, isAuthenticated, activeToken]);

  // API'den takvim ve etkinlikleri yeniden yükle (import sonrası anlık senkron için)
  const reloadCalendarsAndEvents = useCallback(async () => {
    if (!activeToken || !isAuthenticated || !activeUserId) return;
    try {
      const [calendarsResponse, eventsResponse] = await Promise.all([
        calendarsApi.list({ token: activeToken }),
        eventsApi.list({ token: activeToken }),
      ]);
      const apiCalendars = calendarsResponse?.calendars || [];
      const apiEvents = eventsResponse?.events || [];
      const formattedCalendars = apiCalendars.map((calendar) => ({
        ...normalizeCalendarRecord(calendar),
        createdAt: normalizeTimestamp(calendar.createdAt || calendar.created_at),
        updatedAt: normalizeTimestamp(calendar.updatedAt || calendar.updated_at || calendar.createdAt || calendar.created_at),
      }));
      const formattedEvents = apiEvents.map((event) => ({
        ...normalizeEventRecord(event),
        createdAt: normalizeTimestamp(event.createdAt || event.created_at),
        updatedAt: normalizeTimestamp(event.updatedAt || event.updated_at || event.createdAt || event.created_at),
      }));
      setCalendars(formattedCalendars);
      setEvents(formattedEvents);
    } catch (error) {
      console.error('❌ [CalendarContext] reloadCalendarsAndEvents failed:', error);
      setCalendars([]);
      setEvents([]);
    }
  }, [activeUserId, isAuthenticated, activeToken]);

  const visibleCalendars = useMemo(() => {
    return calendars;
  }, [calendars]);

  const visibleEvents = useMemo(() => {
    return events;
  }, [events]);


  const createCalendar = async (calendarData) => {
    // Plus limiti kontrolü
    const userCalendars = calendars;
    
    // Plus kullanıcısı değilse ve limit aşıldıysa Gold bildirimi göster
    if (!isPlusUser && userCalendars.length >= PlusLimits.CALENDARS) {
      // Limit aşıldı - Gold bildirimi göster ve işlemi durdur
      showPlusGoldNotification(i18n.t('shared:plusGoldNotification.features.calendar'), i18n.t('shared:calendarContext.calendarLimitMessage'));
      const error = new Error(i18n.t('shared:calendarContext.calendarLimitError'));
      error.code = 'CALENDAR_LIMIT_EXCEEDED';
      throw error;
    }
    
    const now = new Date().toISOString();
    // UUID formatında ID oluştur (PostgreSQL uyumlu)
    const calendarId = generateUUID();
    const rawCalendar = {
      id: calendarId,
      name: calendarData.name || i18n.t('calendar:defaults.newCalendar'),
      color: calendarData.color || '#ffffff',
      description: calendarData.description || '',
      showSpecialDays: Boolean(calendarData.showSpecialDays),
      createdAt: now,
      updatedAt: now,
    };

    // ONLINE-FIRST: Önce API'ye kaydet, sonra local state güncelle
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        const response = await calendarsApi.upsert({
          token: activeToken,
            calendars: [{
              id: rawCalendar.id,
              name: rawCalendar.name,
              color: rawCalendar.color,
              description: rawCalendar.description,
              showSpecialDays: rawCalendar.showSpecialDays,
              createdAt: rawCalendar.createdAt,
              updatedAt: rawCalendar.updatedAt,
            }],
        });
        
        // API yanıtından gelen veriyi kullan (source of truth)
        if (response?.calendars?.[0] || response?.calendar) {
          const savedCalendar = normalizeCalendarRecord(response.calendars?.[0] || response.calendar);
          setCalendars((prev) => [savedCalendar, ...prev]);
          return savedCalendar;
        }
      } catch (error) {
        console.error('❌ [CalendarContext] Error saving calendar to API:', error);
        throw error; // Hata durumunda işlemi iptal et
      }
    }
    
    // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
    const normalizedCalendar = normalizeCalendarRecord(rawCalendar);
    setCalendars((prev) => [normalizedCalendar, ...prev]);
    return normalizedCalendar;
  };

  const duplicateCalendar = async (calendarId) => {
    try {
      const originalCalendar = calendars.find((calendar) => calendar.id === calendarId);
      if (!originalCalendar) return null;

      const now = new Date().toISOString();
      const newCalendarId = `calendar_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const duplicatedCalendar = normalizeCalendarRecord({
        ...originalCalendar,
        id: newCalendarId,
        createdAt: now,
      });

      setCalendars((prevCalendars) => {
        const updatedCalendars = [duplicatedCalendar, ...prevCalendars];
        return updatedCalendars;
      });

      const relatedEvents = events.filter((event) => event.calendarId === calendarId);
      let duplicatedEvents = [];

      if (relatedEvents.length > 0) {
        const baseTimestamp = Date.now();
        duplicatedEvents = relatedEvents.map((event, index) => normalizeEventRecord({
          ...event,
          id: `event_${baseTimestamp}_${index}_${Math.random().toString(36).substr(2, 6)}`,
          calendarId: newCalendarId,
          createdAt: now,
        }));

        setEvents((prevEvents) => {
          const updatedEvents = [...duplicatedEvents, ...prevEvents];
          return updatedEvents;
        });
      }

      // API'ye kaydet
      if (isAuthenticated && activeToken && activeUserId) {
        try {
          await calendarsApi.upsert({
            token: activeToken,
            calendars: [{
              id: duplicatedCalendar.id,
              name: duplicatedCalendar.name,
              color: duplicatedCalendar.color,
              description: duplicatedCalendar.description,
              createdAt: duplicatedCalendar.createdAt,
              updatedAt: duplicatedCalendar.updatedAt,
            }],
          });
        } catch (error) {
          console.error('Error duplicating calendar in API:', error);
        }

        if (duplicatedEvents.length > 0) {
          try {
            await eventsApi.upsert({
              token: activeToken,
              events: duplicatedEvents.map(event => ({
                id: event.id,
                calendarId: event.calendarId,
                title: event.title,
                description: event.description,
                startDate: event.startDate,
                endDate: event.endDate,
                startTime: event.startTime,
                endTime: event.endTime,
                allDay: event.allDay,
                reminderDay: event.reminderDay,
                reminderHour: event.reminderHour,
                repeatType: event.repeatType,
                createdAt: event.createdAt,
                updatedAt: event.updatedAt,
              })),
            });
          } catch (error) {
            console.error('Error duplicating calendar events in API:', error);
          }
        }
      }
      return { calendar: duplicatedCalendar, events: duplicatedEvents };
    } catch (error) {
      console.error('Error duplicating calendar:', error);
      alert(i18n.t('shared:calendarContext.duplicateFailedAlert', { reason: getApiErrorMessage(error, { fallback: i18n.t('shared:cloudApiErrors.unknownError') }) }));
      return null;
    }
  };

  const updateCalendar = async (updatedCalendar) => {
    const now = new Date().toISOString();
    const calendarPayload = {
      name: updatedCalendar.name || '',
      color: updatedCalendar.color || '#ffffff',
      description: updatedCalendar.description || '',
      updatedAt: now,
    };

    // ONLINE-FIRST: Önce API'ye kaydet
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        const response = await calendarsApi.update({
          token: activeToken,
          calendarId: updatedCalendar.id,
          calendar: calendarPayload,
        });
        
        // API yanıtından gelen veriyi kullan (source of truth)
        if (response?.calendar) {
          const savedCalendar = normalizeCalendarRecord(response.calendar);
          setCalendars((prev) => prev.map((cal) => cal.id === savedCalendar.id ? savedCalendar : cal));
          if (selectedCalendar?.id === savedCalendar.id) {
            setSelectedCalendar(savedCalendar);
          }
          return savedCalendar;
        }
      } catch (error) {
        console.error('❌ [CalendarContext] Error updating calendar in API:', error);
        throw error;
      }
    }
    
    // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
    const normalizedCalendar = normalizeCalendarRecord({
      ...updatedCalendar,
      updatedAt: now,
    });
    setCalendars((prev) => prev.map((cal) => cal.id === normalizedCalendar.id ? normalizedCalendar : cal));
    if (selectedCalendar?.id === normalizedCalendar.id) {
      setSelectedCalendar(normalizedCalendar);
    }
    return normalizedCalendar;
  };

  const deleteCalendar = async (calendarId) => {

    // ONLINE-FIRST: Önce API'den sil
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        await calendarsApi.delete({ token: activeToken, calendarId });
        
        // Related events'i de sil
        const eventsToDelete = events.filter((event) => event.calendarId === calendarId);
        for (const event of eventsToDelete) {
          try {
            await eventsApi.delete({ token: activeToken, eventId: event.id });
          } catch (error) {
            console.error('Error deleting related event:', event.id, error);
          }
        }
        
        // API başarılı olursa local state güncelle
        setCalendars((prev) => prev.filter((calendar) => calendar.id !== calendarId));
        setEvents((prev) => prev.filter((event) => event.calendarId !== calendarId));
        if (selectedCalendar?.id === calendarId) {
          setSelectedCalendar(null);
        }
      } catch (error) {
        console.error('❌ [CalendarContext] Error deleting calendar from API:', error);
        throw error;
      }
    } else {
      // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
      setCalendars((prev) => prev.filter((calendar) => calendar.id !== calendarId));
      setEvents((prev) => prev.filter((event) => event.calendarId !== calendarId));
      if (selectedCalendar?.id === calendarId) {
        setSelectedCalendar(null);
      }
    }
  };

  const selectCalendar = (calendar) => {
    setSelectedCalendar(calendar);
  };

  const createEvent = async (eventData) => {
    let calendarIdToUse = selectedCalendar?.id || eventData.calendarId;

    const weeklyRepeatCount =
      eventData.repeatType === 'weekly'
        ? Math.max(1, parseInt(eventData.weeklyRepeatCount, 10) || 1)
        : 1;
    const monthlyRepeatCount =
      eventData.repeatType === 'monthly'
        ? Math.max(1, parseInt(eventData.monthlyRepeatCount, 10) || 1)
        : 1;
    const YEARLY_REPEAT_SPAN = 30;

    const occurrencesCount =
      eventData.repeatType === 'weekly'
        ? weeklyRepeatCount
        : eventData.repeatType === 'monthly'
        ? monthlyRepeatCount
        : eventData.repeatType === 'yearly'
        ? YEARLY_REPEAT_SPAN
        : 1;

    const eventsToCreate = [];
    const now = new Date().toISOString();

    // Tekrarlı etkinlikler için ortak grup ID'si üret
    const repeatGroupId = occurrencesCount > 1 ? generateUUID() : null;

    for (let index = 0; index < occurrencesCount; index += 1) {
      let startDate = eventData.startDate;
      let endDate = eventData.endDate || eventData.startDate;

      if (eventData.repeatType === 'weekly') {
        const offsetDays = index * 7;
        startDate = shiftDateString(startDate, offsetDays);
        endDate = shiftDateString(endDate, offsetDays);
      } else if (eventData.repeatType === 'monthly') {
        startDate = shiftDateByMonths(startDate, index);
        endDate = shiftDateByMonths(endDate, index);
      } else if (eventData.repeatType === 'yearly') {
        startDate = shiftDateByYears(startDate, index);
        endDate = shiftDateByYears(endDate, index);
      }

      // UUID formatında ID oluştur (PostgreSQL uyumlu)
      const eventId = generateUUID();
      const newEvent = {
        id: eventId,
        calendarId: calendarIdToUse,
        title: eventData.title || i18n.t('calendar:defaults.event'),
        description: eventData.description || '',
        startDate,
        endDate,
        startTime: eventData.startTime || '09:00',
        endTime: eventData.endTime || '10:00',
        allDay: eventData.allDay || false,
        reminderDay: eventData.reminderDay || '0',
        reminderHour: eventData.reminderHour || '09:00',
        repeatType: eventData.repeatType || 'none',
        repeatGroupId,
        location: eventData.location || null,
        locationLat: eventData.locationLat || null,
        locationLon: eventData.locationLon || null,
        createdAt: now,
        updatedAt: now,
      };

      eventsToCreate.push(newEvent);
    }

    // ONLINE-FIRST: Önce API'ye kaydet
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        const response = await eventsApi.upsert({
          token: activeToken,
          events: eventsToCreate,
        });
        
        // API yanıtından gelen veriyi kullan (source of truth)
        if (response?.events && response.events.length > 0) {
          const savedEvents = response.events.map(normalizeEventRecord);
          setEvents((prev) => [...savedEvents, ...prev]);
          return savedEvents[0];
        }
      } catch (error) {
        console.error('❌ [CalendarContext] Error saving events to API:', error);
        throw error;
      }
    }
    
    // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
    const normalizedEvents = eventsToCreate.map(normalizeEventRecord);
    setEvents((prev) => [...normalizedEvents, ...prev]);
    return normalizedEvents[0];
  };

  const updateEvent = async (updatedEvent) => {
    const now = new Date().toISOString();
    const eventPayload = {
      calendarId: updatedEvent.calendarId,
      title: updatedEvent.title || '',
      description: updatedEvent.description || '',
      startDate: updatedEvent.startDate,
      endDate: updatedEvent.endDate || updatedEvent.startDate,
      startTime: updatedEvent.startTime || null,
      endTime: updatedEvent.endTime || null,
      allDay: updatedEvent.allDay,
      reminderDay: updatedEvent.reminderDay || null,
      reminderHour: updatedEvent.reminderHour || null,
      repeatType: updatedEvent.repeatType || 'none',
      location: updatedEvent.location || null,
      locationLat: updatedEvent.locationLat || null,
      locationLon: updatedEvent.locationLon || null,
      updatedAt: now,
    };

    // ONLINE-FIRST: Önce API'ye kaydet
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        const response = await eventsApi.update({
          token: activeToken,
          eventId: updatedEvent.id,
          event: eventPayload,
        });
        
        // API yanıtından gelen veriyi kullan (source of truth)
        if (response?.event) {
          const savedEvent = normalizeEventRecord(response.event);
          setEvents((prev) => prev.map((evt) => evt.id === savedEvent.id ? savedEvent : evt));
          return savedEvent;
        }
      } catch (error) {
        console.error('❌ [CalendarContext] Error updating event in API:', error);
        throw error;
      }
    }
    
    // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
    const normalizedEvent = normalizeEventRecord({
      ...updatedEvent,
      updatedAt: now,
    });
    setEvents((prev) => prev.map((evt) => evt.id === normalizedEvent.id ? normalizedEvent : evt));
    return normalizedEvent;
  };

  const deleteEvent = async (eventId) => {
    const targetEvent = events.find((event) => event.id === eventId);
    // Special day events cannot be deleted (they are generated dynamically)
    if (targetEvent?.isSpecialDay) {
      return;
    }

    // ONLINE-FIRST: Önce API'den sil
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        await eventsApi.delete({ token: activeToken, eventId });
        // API başarılı olursa local state güncelle
        setEvents((prev) => prev.filter((event) => event.id !== eventId));
      } catch (error) {
        console.error('❌ [CalendarContext] Error deleting event from API:', error);
        throw error;
      }
    } else {
      // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
      setEvents((prev) => prev.filter((event) => event.id !== eventId));
    }
  };

  const deleteEventGroup = async (repeatGroupId) => {
    if (!repeatGroupId) return;

    // ONLINE-FIRST: Önce API'den sil
    if (isAuthenticated && activeToken && activeUserId) {
      try {
        await eventsApi.deleteByGroup({ token: activeToken, groupId: repeatGroupId });
        // API başarılı olursa local state güncelle
        setEvents((prev) => prev.filter((event) => event.repeatGroupId !== repeatGroupId));
      } catch (error) {
        console.error('❌ [CalendarContext] Error deleting event group from API:', error);
        throw error;
      }
    } else {
      // Fallback: Kullanıcı giriş yapmamışsa local state güncelle
      setEvents((prev) => prev.filter((event) => event.repeatGroupId !== repeatGroupId));
    }
  };

  // Helper function to pad numbers
  const padNumber = (num) => {
    return String(num).padStart(2, '0');
  };

  // Generate special day events for a specific date
  const getSpecialDayEventsForDate = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    const specialEvents = [];
    SPECIAL_DAY_DEFINITIONS.forEach((definition) => {
      if (definition.month === month && definition.day === day) {
        const startDate = `${year}-${padNumber(definition.month)}-${padNumber(definition.day)}`;
        const hasEndDate = definition.endMonth && definition.endDay;
        let endYear = year;
        if (hasEndDate) {
          endYear = definition.endMonth < definition.month ||
            (definition.endMonth === definition.month && definition.endDay < definition.day)
            ? year + 1 : year;
        }
        const endDate = hasEndDate
          ? `${endYear}-${padNumber(definition.endMonth)}-${padNumber(definition.endDay)}`
          : startDate;
        
        specialEvents.push({
          id: `special_day_${definition.key}_${year}`,
          calendarId: null, // Special days don't belong to a specific calendar
          title: i18n.t(`calendar:specialDays.${definition.titleKey}`),
          description: '',
          startDate,
          endDate,
          startTime: null,
          endTime: null,
          allDay: true,
          reminderDay: '0',
          reminderHour: '09:00',
          repeatType: 'yearly',
          createdAt: new Date(`${startDate}T00:00:00`).toISOString(),
          isSpecialDay: true, // Mark as special day event
        });
      }
    });
    return specialEvents;
  };

  const getEventsForDate = (date, calendarId = null) => {
    let filteredEvents = visibleEvents.filter((event) => {
      if (calendarId && event.calendarId !== calendarId) return false;

      const start = new Date(event.startDate);
      const end = event.endDate ? new Date(event.endDate) : new Date(event.startDate);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      const targetDate = new Date(date);
      targetDate.setHours(12, 0, 0, 0);

      return targetDate >= start && targetDate <= end;
    });

    // Deduplicate by id (avoid duplicate key when same event appears twice in state)
    const seen = new Set();
    filteredEvents = filteredEvents.filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });

    // If calendarId is provided, check if that calendar has showSpecialDays enabled
    if (calendarId) {
      const calendar = calendars.find(c => c.id === calendarId);
      if (calendar?.showSpecialDays) {
        const specialDayEvents = getSpecialDayEventsForDate(date);
        filteredEvents = [...filteredEvents, ...specialDayEvents];
      }
    }

    return filteredEvents;
  };

  const getEventsForCalendar = (calendarId) => {
    return visibleEvents.filter((event) => event.calendarId === calendarId);
  };

  const clearAllCalendarData = async () => {
    try {
      // API'den tüm verileri sil
      if (isAuthenticated && activeToken && activeUserId) {
        for (const event of events) {
          try {
            await eventsApi.delete({ token: activeToken, eventId: event.id });
          } catch (error) {
            console.error('Takvim olayı silme hatası:', error);
          }
        }
        for (const calendar of calendars) {
          try {
            await calendarsApi.delete({ token: activeToken, calendarId: calendar.id });
          } catch (error) {
            console.error('Takvim silme hatası:', error);
          }
        }
      }

      setEvents([]);
      setCalendars([]);
      setSelectedCalendar(null);
    } catch (error) {
      console.error('Takvim verileri temizlenirken hata:', error);
      throw error;
    }
  };

  // Üyelik bilgilerini hesapla
  const membershipType = useMemo(() => getMembershipType(activeUser), [activeUser]);
  const membershipLimits = useMemo(() => getLimitsForMembership(membershipType), [membershipType]);
  
  // Takvim limiti bilgileri
  const userCalendarCount = useMemo(() => 
    calendars.length, 
    [calendars]
  );
  const maxCalendarCount = membershipLimits.maxCalendars;
  const canCreateMoreCalendars = maxCalendarCount === Infinity || userCalendarCount < maxCalendarCount;
  const remainingCalendars = maxCalendarCount === Infinity ? null : Math.max(0, maxCalendarCount - userCalendarCount);
  
  // Recurring events kontrolü
  const canUseRecurringEvents = membershipLimits.recurringEvents;

  const value = {
    calendars: visibleCalendars,
    selectedCalendar,
    events: visibleEvents,
    allCalendars: calendars,
    allEvents: events,
    createCalendar,
    duplicateCalendar,
    updateCalendar,
    deleteCalendar,
    selectCalendar,
    createEvent,
    updateEvent,
    deleteEvent,
    deleteEventGroup,
    getEventsForDate,
    getEventsForCalendar,
    clearAllCalendarData,
    reloadCalendarsAndEvents,
    // Üyelik ve limit bilgileri
    userCalendarCount,
    maxCalendarCount,
    canCreateMoreCalendars,
    remainingCalendars,
    canUseRecurringEvents,
    membershipType,
  };

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>;
};

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};

